import React, { useEffect, useState, useRef } from 'react'
import { AlertDialog } from '@base-ui/react/alert-dialog'
import {
  ArrowRightFromLine,
  ArrowLeftToLine,
  Users,
  MessageCircle,
  Clock,
  Lock,
  Car
} from 'lucide-react'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card } from './ui/card'
import { appwriteDatabaseId, appwriteRidesTableId, appwriteTables, getAppwriteUser } from '../appwrite'

function appwriteRowToRide(row, currentUserId) {
  var permissions = Array.isArray(row.$permissions) ? row.$permissions : []
  var ownerId = permissions.some(function (permission) { return permission.indexOf('user:' + currentUserId) !== -1 }) ? currentUserId : ''
  return {
    _id: 'appwrite-' + row.$id,
    sender: row.contactPhone,
    timestamp: row.$createdAt,
    groupJid: row.groupId,
    route: row.route,
    text: (row.kind === 'request' ? '*BUSCO CUPO*' : '*OFREZCO CUPO*') + '\nDirección: ' + row.direction + '\nHora: ' + row.time + '\nRuta: ' + row.route + '\nCupos: ' + row.seats,
    parsed: {
      direction: row.direction === 'Salida' ? 'SALIDA' : 'ENTRADA',
      time: row.time,
      seats: String(row.seats),
      price: row.price ? '$' + row.price : '',
      isRequest: row.kind === 'request'
    },
    appwriteRowId: row.$id,
    appwriteOwnerId: ownerId
  }
}

function formatTimestamp(ts) {
  if (!ts) return ''
  var d = new Date(ts)
  var now = new Date()
  var today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  var dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  var dayDiff = Math.floor((today - dateOnly) / 86400000)
  var timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (dayDiff === 0) return 'Hoy ' + timeStr
  if (dayDiff === 1) return 'Ayer ' + timeStr
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' }) + ' ' + timeStr
}

function formatWhatsAppText(text) {
  if (!text) return ''
  return text
    .replace(/\*(.*?)\*/g, '<b>$1</b>')
    .replace(/_(.*?)_/g, '<i>$1</i>')
    .replace(/~(.*?)~/g, '<del>$1</del>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
}

function RideCard({ ride, onEdit, onDelete }) {
  var [msgOpen, setMsgOpen] = useState(false)

  var direction = ride.parsed?.direction
  var isSalida = direction === 'SALIDA'
  var timeText = ride.parsed?.time || ''
  var price = ride.parsed?.price || ''
  var seats = ride.parsed?.seats
  var phone = (ride.sender || '').split('@')[0]
  var isLID = ride.sender && ride.sender.endsWith('@lid')
  var isRequest = !!ride.parsed?.isRequest
  var canManage = !!ride.appwriteRowId && !!ride.appwriteOwnerId

  var displayMsgText = ride.text
  if (ride.text && ride.text.length < 50 && ride.updates && ride.updates.length > 0) {
    var sorted = [].concat(ride.updates).sort(function (a, b) { return (b.text || '').length - (a.text || '').length })
    if (sorted[0] && sorted[0].text && sorted[0].text.length > ride.text.length) {
      displayMsgText = sorted[0].text
    }
  }
  var cleanMainText = (displayMsgText || '').split('\n\n(Actualización:')[0]

  function handleContact() {
    var article = timeText.match(/^1:/i) ? 'la' : 'las'
    var msg = encodeURIComponent(isRequest ? 'Hola, vi que buscas cupo. ¿Aún necesitas transporte?' : 'Hola, ¿aún tiene cupo para ' + article + ' ' + timeText + '?')
    window.open('https://wa.me/' + phone + '?text=' + msg, '_blank')
  }

  function handleAuth() {
    if (typeof window.showAuthModal === 'function') {
      window.showAuthModal()
    }
  }

  return (
    <Card className="bg-white shadow-sm overflow-hidden !py-0 !gap-0">
      <div className="p-5 pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={[
              'w-8 h-8 flex items-center justify-center rounded-xl shrink-0',
              isSalida ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
            ].join(' ')}>
              {isSalida ? <ArrowRightFromLine className="size-4" strokeWidth={2.5} /> : <ArrowLeftToLine className="size-4" strokeWidth={2.5} />}
            </div>
            <div className="min-w-0">
              <div className={[
                'text-sm font-bold uppercase tracking-wider',
                isSalida ? 'text-blue-600' : 'text-purple-600'
              ].join(' ')}>
                {isSalida ? 'Salida' : 'Entrada'} {timeText}
              </div>
              <div className="text-xs text-slate-500">
                {isSalida ? 'De ESPOL' : 'A ESPOL'}
              </div>
            </div>
          </div>
          {isRequest ? (
            <Badge className="h-6 rounded-lg bg-purple-100 px-2 text-[10px] font-bold uppercase tracking-wide text-purple-700 hover:bg-purple-100">
              Solicita cupo
            </Badge>
          ) : (
            <div className="text-lg font-extrabold text-green-600 tracking-tight leading-none -mt-1 shrink-0">{price}</div>
          )}
        </div>
      </div>

      <div className="px-5 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5 items-center flex-wrap">
            {seats && (
              <Badge variant="secondary" className="h-7 rounded-lg gap-1 px-2 py-0 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-100">
                <Users className="size-3" strokeWidth={2.5} />
                {seats}{!isNaN(seats) && (isRequest
                  ? (parseInt(seats) === 1 ? ' pasajero' : ' pasajeros')
                  : (parseInt(seats) === 1 ? ' cupo' : ' cupos'))}
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 rounded-lg text-[10px] font-semibold text-slate-600 bg-gray-50 hover:bg-gray-100 shrink-0"
              onClick={function (e) { e.stopPropagation(); setMsgOpen(!msgOpen) }}
            >
              <MessageCircle className="size-3" strokeWidth={2} />
              Mensaje
            </Button>
            {canManage && <Button variant="ghost" size="sm" className="h-7 rounded-lg text-[10px] font-semibold text-slate-600 bg-gray-50 hover:bg-gray-100 shrink-0" onClick={function (e) { e.stopPropagation(); onEdit(ride) }}>Editar</Button>}
            {canManage && <Button variant="ghost" size="sm" className="h-7 rounded-lg text-[10px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 shrink-0" onClick={function (e) { e.stopPropagation(); onDelete(ride) }}>Eliminar</Button>}
          </div>
        </div>

        {msgOpen && (
          <div className="pt-3">
            <div className="whitespace-pre-wrap bg-gray-50 p-3.5 rounded-xl text-sm leading-relaxed text-slate-700 break-words overflow-x-hidden">
              <span dangerouslySetInnerHTML={{ __html: formatWhatsAppText(cleanMainText.trim()) }} />
              <div className="text-right text-[11px] opacity-50 mt-1.5 font-medium">
                {formatTimestamp(ride.timestamp)}
              </div>
            </div>

            {ride.updates && ride.updates.length > 0 && (
              <details className="mt-2.5">
                <summary className="cursor-pointer text-xs text-gray-500 flex items-center gap-1.5 pl-1 list-none">
                  <Clock className="size-3" strokeWidth={2.5} />
                  Actualizaciones ({ride.updates.length})
                </summary>
                <div className="mt-2 pl-5 border-l-2 border-gray-200 ml-1">
                  {ride.updates.map(function (u, i) {
                    var uSeats = u.parsed && u.parsed.seats ? u.parsed.seats : null
                    var seatsLabel = uSeats ? (parseInt(uSeats) === 1 ? '1 cupo' : uSeats + ' cupos') : ''
                    return (
                      <div key={i} className="mb-2 text-xs">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-bold text-slate-400 text-[11px]">{formatTimestamp(u.timestamp)}</span>
                          {uSeats && <span className="text-[10px] font-extrabold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{seatsLabel}</span>}
                        </div>
                        <div className="whitespace-pre-wrap opacity-80 text-slate-600 break-words">
                          <span dangerouslySetInnerHTML={{ __html: formatWhatsAppText((u.text || '').trim()) }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </details>
            )}
          </div>
        )}

      </div>

      <div className="px-5 pb-5">
        {ride.sender === null || isLID ? (
          <Button
            onClick={handleAuth}
            className="w-full bg-black hover:bg-black/80 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer"
          >
            <Lock className="size-3.5" strokeWidth={2} />
            Verificar acceso para contactar
          </Button>
        ) : (
          <Button
            onClick={handleContact}
            className="w-full bg-[#25d366] hover:bg-[#20bd5a] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer border-0"
          >
            <MessageCircle className="size-4 fill-current" strokeWidth={2} />
            Contactar por WhatsApp
          </Button>
        )}
      </div>
    </Card>
  )
}

var RideCardMemo = React.memo(RideCard)

function DeleteRideDialog({ ride, deleting, onOpenChange, onConfirm }) {
  return (
    <AlertDialog.Root open={!!ride} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="fixed inset-0 z-50 bg-slate-950/45 backdrop-blur-[1px]" />
        <AlertDialog.Viewport className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <AlertDialog.Popup className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl outline-none">
            <AlertDialog.Title className="text-base font-bold text-slate-900">¿Eliminar este viaje?</AlertDialog.Title>
            <AlertDialog.Description className="mt-2 text-sm leading-relaxed text-slate-600">
              Esta acción no se puede deshacer.
            </AlertDialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <AlertDialog.Close className="h-9 rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200">
                Cancelar
              </AlertDialog.Close>
              <Button variant="destructive" className="h-9 rounded-lg px-3 text-sm" disabled={deleting} onClick={onConfirm}>
                {deleting ? 'Eliminando…' : 'Eliminar'}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

export default function RideList({ onEdit }) {
  var [rides, setRides] = useState([])
  var [appwriteRides, setAppwriteRides] = useState([])
  var [noGroup, setNoGroup] = useState(true)
  var [rideToDelete, setRideToDelete] = useState(null)
  var [deletingRide, setDeletingRide] = useState(false)
  var [rideType, setRideType] = useState(typeof window !== 'undefined' ? (window.currentRideType || 'todos') : 'todos')
  var listRef = useRef(null)

  useEffect(function () {
    function update() {
      var sg = typeof window !== 'undefined' ? window.selectedGroup : ''
      setNoGroup(!sg)
      if (Array.isArray(window.__filteredRides)) {
        setRides(window.__filteredRides)
      } else if (Array.isArray(window.allRides)) {
        setRides(window.allRides)
      }
    }

    var unsub = null
    if (window.legacyStore && typeof window.legacyStore.onFilteredRides === 'function') {
      unsub = window.legacyStore.onFilteredRides(function (filteredRides) {
        setNoGroup(!window.selectedGroup)
        if (Array.isArray(filteredRides)) {
          setRides(filteredRides)
        }
      })
    }

    function handleEvent(ev) {
      if (ev.detail && Array.isArray(ev.detail.rides)) {
        setNoGroup(!window.selectedGroup)
        setRides(ev.detail.rides)
      }
    }
    window.addEventListener('filtered-rides-updated', handleEvent)
    update()

    return function () {
      if (typeof unsub === 'function') unsub()
      window.removeEventListener('filtered-rides-updated', handleEvent)
    }
  }, [])

  useEffect(function () {
    function handleRideType(event) { setRideType(event.detail || window.currentRideType || 'todos') }
    window.addEventListener('ride-type-filter-updated', handleRideType)
    return function () { window.removeEventListener('ride-type-filter-updated', handleRideType) }
  }, [])

  useEffect(function () {
    var active = true
    async function loadAppwriteRides() {
      if (!appwriteDatabaseId || !appwriteRidesTableId) return
      try {
        var user = await getAppwriteUser()
        var response = await appwriteTables.listRows({
          databaseId: appwriteDatabaseId,
          tableId: appwriteRidesTableId,
          total: false
        })
        if (active) setAppwriteRides((response.rows || []).map(function (row) { return appwriteRowToRide(row, user.$id) }))
      } catch (error) {
        console.warn('[RideList] No se pudieron cargar los viajes de Appwrite.', error)
      }
    }
    function handleAppwriteRides(event) {
      var newRides = event.detail && Array.isArray(event.detail.rides) ? event.detail.rides : []
      var replaceId = event.detail && event.detail.replaceId
      setAppwriteRides(function (current) {
        var newIds = new Set(newRides.map(function (ride) { return ride._id }))
        return newRides.concat(current.filter(function (ride) { return !newIds.has(ride._id) && ride._id !== replaceId }))
      })
    }
    loadAppwriteRides()
    window.addEventListener('appwrite-rides-updated', handleAppwriteRides)
    return function () {
      active = false
      window.removeEventListener('appwrite-rides-updated', handleAppwriteRides)
    }
  }, [])

  var selectedGroup = typeof window !== 'undefined' ? window.selectedGroup : ''
  var visibleRides = rides.concat(appwriteRides.filter(function (ride) { return ride.groupJid === selectedGroup }))
    .filter(function (ride) {
      return rideType === 'todos' || (rideType === 'request' ? !!ride.parsed?.isRequest : !ride.parsed?.isRequest)
    })

  function requestDeleteRide(ride) {
    setRideToDelete(ride)
  }

  async function deleteRide() {
    if (!rideToDelete || deletingRide) return
    setDeletingRide(true)
    try {
      await appwriteTables.deleteRow({ databaseId: appwriteDatabaseId, tableId: appwriteRidesTableId, rowId: rideToDelete.appwriteRowId })
      setAppwriteRides(function (current) { return current.filter(function (item) { return item._id !== rideToDelete._id }) })
      setRideToDelete(null)
    } catch (error) {
      window.alert('No se pudo eliminar el viaje.')
    } finally {
      setDeletingRide(false)
    }
  }

  if (noGroup) {
    return (
      <div ref={listRef} className="flex flex-col items-center justify-center min-h-[300px] px-6 text-center">
        <div className="size-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-5">
          <Car className="size-7 text-slate-400" strokeWidth={1.5} />
        </div>
        <div className="text-lg font-bold text-slate-800 mb-2">Selecciona un grupo</div>
        <div className="text-sm text-slate-500 leading-relaxed max-w-[260px]">
          Elige un grupo usando el selector de la parte superior para ver los viajes disponibles.
        </div>
      </div>
    )
  }

  if (!Array.isArray(visibleRides) || !visibleRides.length) {
    return (
      <div ref={listRef} className="p-[60px_20px] text-center text-slate-400">
        No hay viajes disponibles para este grupo
      </div>
    )
  }

  try {
    return (
      <>
        <div ref={listRef} className="w-full px-4 py-2 space-y-3">
          {visibleRides.map(function (ride, i) {
            return <RideCardMemo key={ride._id || i} ride={ride} onEdit={onEdit} onDelete={requestDeleteRide} />
          })}
        </div>
        <DeleteRideDialog
          ride={rideToDelete}
          deleting={deletingRide}
          onOpenChange={function (open) { if (!open && !deletingRide) setRideToDelete(null) }}
          onConfirm={deleteRide}
        />
      </>
    )
  } catch (renderErr) {
    console.error('[RideList] render error:', renderErr)
    return (
      <div ref={listRef} className="p-[60px_20px] text-center text-slate-400">
        Error al mostrar viajes
      </div>
    )
  }
}
